import { type IEventEmitter, EventEmitter } from '../system';
import { getCurrentZoomValue } from 'components/zoom-bar/functions';

const TRANSITION_DURATION = '0.1s';
const BODY_CLASS = 'body_drag-manager-start';

export interface IDragManager extends IEventEmitter {
    setElementSelector(selector: string): void;
    setElement(element: HTMLElement): void;
    setAvatarFunction(avatarFunction: (e: Event) => IDragAvatar): void;
    run(): void;
    stop(): void;
    destroy(): void;
}

export interface IDragAvatar extends HTMLElement {
    rollback(): Promise<any>;
    apply(): Promise<void>;
    busy?: boolean;
    cancelMovePromise?: Promise<boolean>;
}

export interface IAvatarIndexes {
    first?: number;
    previous?: number;
    current?: number;
}

interface IDragObject {
    elem?: HTMLElement;
    avatar?: any;
    initEventScreenX?: number;
    initEventScreenY?: number;
    downY?: number;
    downX?: number;
}

export class DragManager extends EventEmitter implements IDragManager {
    protected elementSelector = null;
    protected element: HTMLElement = null;
    protected avatarFunction: (e: Event) => IDragAvatar;
    protected dragObject: IDragObject = {};
    protected isMoved = false;
    protected active: boolean = true;
    private removeListeners: () => void;

    constructor(element: string | HTMLElement = null) {
        super();
        if (typeof element === 'string') {
            this.setElementSelector(element);
        } else if (element instanceof HTMLElement) {
            this.setElement(element);
        }
        this.initListeners();
    }

    stop() {
        this.active = false;
    }

    run() {
        this.active = true;
    }

    public setElementSelector(selector: string) {
        this.elementSelector = selector;
        this.element = null;
    }

    public setElement(element: HTMLElement) {
        this.element = element;
        this.elementSelector = null;
    }

    public setAvatarFunction(avatarFunction) {
        this.avatarFunction = avatarFunction;
    }

    public onMouseUp(_e: MouseEvent) {
        if (!this.active) {
            return;
        }
        if (this.dragObject && this.dragObject.avatar) {
            this.emit('finish', this.dragObject && this.dragObject.avatar);
        } else if (this.dragObject && this.dragObject.elem) {
            this.dragObject = {};
        }
        this.isMoved = false;
    }

    public onMouseMove(e: MouseEvent) {
        if (!this.active || !this.dragObject.elem) {
            return;
        }
        if (!this.dragObject.avatar) {
            const moveX = e.pageX - this.dragObject.downX;
            const moveY = e.pageY - this.dragObject.downY;
            if (Math.abs(moveX) < 3 && Math.abs(moveY) < 3) {
                return;
            }
            this.emit('prestart');
            this.dragObject.avatar = this.avatarFunction(e);
            if (this.dragObject.avatar) {
                document.body.classList.add(BODY_CLASS);
                this.dragObject.avatar.rollback = () => {
                    return this.rollbackAvatar();
                };
                this.dragObject.avatar.apply = () => {
                    return this.applyAvatar();
                };
                this.dragObject.avatar.style.transition = `all ${TRANSITION_DURATION}`;
                document.body.appendChild(this.dragObject.avatar);
                this.emit('start', this.dragObject.avatar);
            } else {
                this.dragObject = {};
            }
        } else {
            this.emit('move', this.dragObject.avatar);
        }

        window.requestAnimationFrame(() => {
            if (this.dragObject.avatar) {
                const x = DragManager.dimWindow2Document(e.screenX - this.dragObject.initEventScreenX);
                const y = DragManager.dimWindow2Document(e.screenY - this.dragObject.initEventScreenY);
                this.dragObject.avatar.style.transform = `translate(${x}px, ${y}px)`;
            }
        });
    }

    public onMouseDown(e: MouseEvent) {
        if (!this.active || this.dragObject.elem) {
            return;
        }
        const elem = this.findClosestElement(e.target as HTMLElement);
        if (elem) {
            this.dragObject.elem = elem;
            this.dragObject.initEventScreenX = e.screenX;
            this.dragObject.initEventScreenY = e.screenY;
            this.dragObject.downX = e.pageX;
            this.dragObject.downY = e.pageY;
            this.isMoved = true;
            e.preventDefault();
        }
    }

    public initListeners() {
        const mousemove = this.onMouseMove.bind(this);
        const mouseup = this.onMouseUp.bind(this);
        const mousedown = this.onMouseDown.bind(this);

        document.addEventListener('mousemove', mousemove);
        document.addEventListener('mouseup', mouseup);
        document.addEventListener('mousedown', mousedown);

        this.removeListeners = function () {
            document.removeEventListener('mousemove', mousemove);
            document.removeEventListener('mouseup', mouseup);
            document.removeEventListener('mousedown', mousedown);
        };
    }

    public destroy() {
        if (this.removeListeners) {
            this.removeListeners();
        }
        this.clear();
    }

    public static getCoords(elem) {
        const box = (elem && elem.getBoundingClientRect()) || {};
        return {
            top: DragManager.dimWindow2Document(((box && box.top) || 0) + window.pageYOffset),
            left: DragManager.dimWindow2Document(((box && box.left) || 0) + window.pageXOffset),
            bottom: DragManager.dimWindow2Document(((box && box.bottom) || 0) + window.pageYOffset),
            right: DragManager.dimWindow2Document(((box && box.right) || 0) + window.pageXOffset)
        };
    }

    protected childOf(/*child node*/ c, /*parent node*/ p) {
        while ((c = c.parentNode) && c !== p);
        return !!c;
    }

    protected findClosestElement(target: HTMLElement) {
        let element = null;
        if (this.element) {
            if (this.childOf(target, this.element)) {
                element = this.element;
            }
        } else if (this.elementSelector) {
            element = target.closest(this.elementSelector);
        }
        return element;
    }

    private static dimWindow2Document(value: number): number {
        return (value * 100) / getCurrentZoomValue();
    }

    private rollbackAvatar(): Promise<void> {
        let resolvePromise, rejectPromise;
        const promise = new Promise<void>((resolve, reject) => {
            rejectPromise = reject;
            resolvePromise = resolve;
        });
        // @to-do: translate back is not working right
        /*
        const onTransitionEnd = () => {
            console.log("!!!!!!!!!!!!!!!!!!1REMOVE onTransitionEnd");
            this.dragObject.avatar.removeEventListener('transitionend', onTransitionEnd);
            this.clear();
            resolvePromise();
        };

        if(!this.dragObject || !this.dragObject.avatar) {
            rejectPromise();
        } else {
            this.dragObject.avatar.addEventListener('transitionend', onTransitionEnd);
            console.log("!!!!!!!!!!!!!!!!!!START Transition");
            this.dragObject.avatar.style.transform = 'translate(0px, 0px)';
        }
        */

        if (!this.dragObject || !this.dragObject.avatar) {
            rejectPromise();
        } else {
            this.clear();
            resolvePromise();
        }
        return promise;
    }

    private applyAvatar() {
        let resolvePromise, rejectPromise;
        const promise = new Promise<void>((resolve, reject) => {
            rejectPromise = reject;
            resolvePromise = resolve;
        });
        if (!this.dragObject || !this.dragObject.avatar) {
            rejectPromise();
        } else {
            this.clear();
            resolvePromise();
        }

        return promise;
    }

    clear() {
        if (this.dragObject && this.dragObject.avatar) {
            document.body.removeChild(this.dragObject.avatar);
            delete this.dragObject.avatar;
            this.dragObject = {};
        }
        document.body.classList.remove(BODY_CLASS);
    }
}
